import {useParams} from "react-router-dom";
import {useAuth} from "@/context/use-auth.tsx";
import {useCallback, useEffect, useState} from "react";
import {
    cancelOrder,
    completeOrder,
    getOrderAuditLogs,
    getOrderDetails,
    OrderAuditLog
} from "@/services/order-services.ts";
import {Text} from "@/components/tailwind/text";
import {OrderDetails} from "@/models/order.ts";
import {Card} from "@/components/ui/card.tsx";
import {Avatar, AvatarFallback} from "@/components/ui/avatar.tsx";
import {
    Activity,
    AlertTriangle,
    CalendarIcon,
    CheckCircle,
    ChevronDown,
    ChevronUp,
    ClipboardList,
    Clock,
    Coins,
    FolderInput,
    Hash,
    ListTodo,
    MessageSquare,
    User,
    UserCog
} from "lucide-react";
import {Heading, Subheading} from "@/components/tailwind/heading.tsx";
import {useCommonTranslation, useOrdersTranslation} from "@/i18n/hooks";
import {formatDateString, formatTimeString} from "@/lib/utils.ts";
import {Divider} from "@/components/tailwind/divider.tsx";
import {Button} from "@/components/tailwind/button.tsx";
import OrderActionsButton from "@/components/order-actions-button.tsx";
import {Badge} from "@/components/tailwind/badge";
import OrderEditDialog from "@/components/order-edit-dialog.tsx";
import {OrderTimesTable} from "@/components/order-times-table";
import {OrderTimeDialog} from "@/components/order-time-dialog";
import {OrderTimeDeleteDialog} from "@/components/order-time-delete-dialog";
import {OrderTime, OrderTimeFormData} from "@/models/order-time";
import {createOrderTime, deleteOrderTime, getOrderTimes, updateOrderTime} from "@/services/order-time-services";
import {useToast} from "@/hooks/use-toast.ts";
import {OrderCostsSection} from "@/components/order-costs-section";
import {useUserSettingsStore} from "@/stores/user-settings";
import OrderCompleteDialog from "@/components/order-complete-dialog";
import {ReopenOrderDialog} from "@/components/order-reopen-dialog";
import {OrderSendEmailDialog} from "@/components/order-send-email-dialog";
import {getOrganizationDetails} from "@/services/organization-details-services";
import {getEmailCapability} from "@/services/email-config-services";
import {EmailSendLogEntry, getOrderEmailLogs} from "@/services/email-send-log-services";
import {Mail} from "lucide-react";
import log from '@/lib/logger'

export default function OrderDetailsPage() {
    const {t} = useOrdersTranslation()
    const {t: tCommon} = useCommonTranslation()
    const {orderId} = useParams()
    const {token} = useAuth()
    const [isLoading, setIsLoading] = useState(true)
    const [orderDetails, setOrderDetails] = useState<OrderDetails | null>(null)
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [orderTimes, setOrderTimes] = useState<OrderTime[]>([]);
    const [isOrderTimeDialogOpen, setIsOrderTimeDialogOpen] = useState(false);
    const [isOrderTimeDeleteDialogOpen, setIsOrderTimeDeleteDialogOpen] = useState(false);
    const [editingOrderTime, setEditingOrderTime] = useState<OrderTime | undefined>();
    const [deletingOrderTime, setDeletingOrderTime] = useState<OrderTime | null>(null);
    const {toast} = useToast();
    const {tableSettings} = useUserSettingsStore();
    const [isCompleteDialogOpen, setIsCompleteDialogOpen] = useState(false);
    const [isReopenDialogOpen, setIsReopenDialogOpen] = useState(false);
    const [auditLogs, setAuditLogs] = useState<OrderAuditLog[]>([]);
    const [isAuditLogsExpanded, setIsAuditLogsExpanded] = useState(false);
    const [emailLogs, setEmailLogs] = useState<EmailSendLogEntry[]>([]);
    const [isEmailLogsExpanded, setIsEmailLogsExpanded] = useState(false);
    const [hasOrganizationDetails, setHasOrganizationDetails] = useState(false);
    const [isSendEmailDialogOpen, setIsSendEmailDialogOpen] = useState(false);
    const [emailCapabilityAvailable, setEmailCapabilityAvailable] = useState(false);
    const [costsRefreshTrigger, setCostsRefreshTrigger] = useState(0);

    function handleCancelOrder() {
        const cancelOrderRequest = cancelOrder(token, orderId)

        cancelOrderRequest
            .then((orderDetails) => {
                if (orderDetails)
                    setOrderDetails(orderDetails)

                toast({
                    title: t("orderDetails.toast.success.title"),
                    description: t("orderDetails.toast.success.description")
                })
            })
            .catch((errorDetails) => {
                log.warn("Unable to delete order. Error details: ", errorDetails)
                toast({
                    title: t("orderDetails.toast.error.title"),
                    description: t("orderDetails.toast.error.description"),
                    variant: "destructive"
                })
            })
    }

    function handleEditOrder() {
        setIsEditDialogOpen(true);
    }

    function handleCompleteOrder() {
        // Öffne IMMER den Dialog - keine Validierung hier!
        setIsCompleteDialogOpen(true);
    }

    function handleReopenOrder() {
        setIsReopenDialogOpen(true);
    }

    async function handleReopenSuccess() {
        // Reload all data after successful reopen
        try {
            const [orderDetails] = await Promise.all([
                getOrderDetails(token, orderId!)
            ]);

            if (orderDetails) {
                setOrderDetails(orderDetails);
                // Reload order times and audit logs
                await loadOrderTimes();
                await loadAuditLogs();
            }
        } catch (error) {
            log.error("Error reloading data after reopen:", error);
        }
    }

    async function handleConfirmCompleteOrder(
        accountingDate: string,
        donatePointsToSocialAccount: boolean
    ) {
        if (!orderId || !token) return;

        try {
            const completedOrder = await completeOrder(
                token, orderId, accountingDate, donatePointsToSocialAccount
            );
            if (completedOrder) {
                setOrderDetails(completedOrder);
                // Reload audit logs to show the completion entry
                await loadAuditLogs();
                toast({
                    title: t("orderDetail.complete.toast.success.title"),
                    description: t("orderDetail.complete.toast.success.description")
                });
                setIsCompleteDialogOpen(false);
            }
        } catch (error: any) {
            // Do not close the dialog: preserve the entered date so the user can correct it.
            if (error.validationIssues?.length > 0) {
                const messages = error.validationIssues.map((v: any) => {
                    const key = v.message?.replace('orderForm.edit.errors.', '') || v.message;
                    return t(`orderForm.edit.errors.${key}`, {defaultValue: v.message});
                }).join(', ');
                toast({
                    title: t("orderDetail.complete.validation.title"),
                    description: messages,
                    variant: "destructive"
                });
            } else if (error.detail) {
                toast({
                    title: t("orderDetail.complete.validation.title"),
                    description: error.detail,
                    variant: "destructive"
                });
            } else {
                toast({
                    title: t("orderDetail.complete.toast.error.title"),
                    description: t("orderDetail.complete.toast.error.description"),
                    variant: "destructive"
                });
            }
        }
    }

    // OrderTime handlers
    const loadOrderTimes = useCallback(async (sortBy?: string, sortOrder?: string) => {
        if (!orderId || !token) return;

        try {
            // Use provided sorting or default to stored preferences or default values
            const finalSortBy = sortBy || tableSettings.orderTimesSortBy || 'date';
            const finalSortOrder = sortOrder || tableSettings.orderTimesSortOrder || 'desc';

            const times = await getOrderTimes(token, orderId, finalSortBy, finalSortOrder);
            if (times) {
                setOrderTimes(times);
            }
        } catch (error) {
            log.error("Error loading order times with sorting:", error);

            // If sorting failed, try loading without sorting parameters
            try {
                const times = await getOrderTimes(token, orderId);
                if (times) {
                    setOrderTimes(times);
                    // Only show toast if we were explicitly trying to sort (not on initial load)
                    if (sortBy || sortOrder) {
                        toast({
                            title: t("orderDetail.times.toast.sortingUnavailable.title"),
                            description: t("orderDetail.times.toast.sortingUnavailable.description")
                        });
                    }
                }
            } catch (fallbackError) {
                log.error("Error loading order times without sorting:", fallbackError);
                toast({
                    title: t("orderDetail.times.toast.loadError.title"),
                    description: t("orderDetail.times.toast.loadError.description"),
                    variant: "destructive"
                });
            }
        }
    }, [orderId, tableSettings.orderTimesSortBy, tableSettings.orderTimesSortOrder, toast, t]);

    // Load audit logs
    const loadAuditLogs = useCallback(async () => {
        if (!orderId || !token) return;

        try {
            const logs = await getOrderAuditLogs(token, orderId);
            if (logs) {
                // Sort audit logs in reverse chronological order (newest first)
                const sortedLogs = logs.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
                setAuditLogs(sortedLogs);
            }
        } catch (error) {
            log.error("Error loading audit logs:", error);
        }
    }, [orderId, token]);

    const loadEmailLogs = useCallback(async () => {
        if (!orderId || !token) return;

        try {
            const logs = await getOrderEmailLogs(token, orderId);
            if (logs) {
                setEmailLogs(logs);
            }
        } catch (error) {
            log.error("Error loading email logs:", error);
        }
    }, [orderId, token]);

    function handleAddOrderTime() {
        setEditingOrderTime(undefined);
        setIsOrderTimeDialogOpen(true);
    }

    function handleEditOrderTime(orderTime: OrderTime) {
        setEditingOrderTime(orderTime);
        setIsOrderTimeDialogOpen(true);
    }

    function handleDeleteOrderTime(orderTime: OrderTime) {
        setDeletingOrderTime(orderTime);
        setIsOrderTimeDeleteDialogOpen(true);
    }

    async function handleSaveOrderTime(formData: OrderTimeFormData) {
        if (!orderId || !token) return;

        try {
            if (editingOrderTime) {
                // Update existing order time
                await updateOrderTime(token, orderId, editingOrderTime.id, formData);
                toast({
                    title: t("orderDetail.times.toast.updated.title"),
                    description: t("orderDetail.times.toast.updated.description")
                });
            } else {
                // Create new order time
                await createOrderTime(token, orderId, formData);
                toast({
                    title: t("orderDetail.times.toast.created.title"),
                    description: t("orderDetail.times.toast.created.description")
                });
            }

            // Reload order details to update admin fees and other calculations
            const updatedOrderDetails = await getOrderDetails(token, orderId);
            if (updatedOrderDetails) {
                setOrderDetails(updatedOrderDetails);
            }
            
            // Trigger costs refresh
            setCostsRefreshTrigger(prev => prev + 1);
            
            await loadOrderTimes();
            await loadAuditLogs();
            setIsOrderTimeDialogOpen(false);
            setEditingOrderTime(undefined);
        } catch (error) {
            log.error("Error saving order time:", error);
            toast({
                title: t("orderDetail.times.toast.error.title"),
                description: t("orderDetail.times.toast.error.description"),
                variant: "destructive"
            });
        }
    }

    async function handleConfirmDeleteOrderTime(orderTime: OrderTime) {
        if (!orderId || !token) return;

        try {
            await deleteOrderTime(token, orderId, orderTime.id);
            toast({
                title: t("orderDetail.times.toast.deleted.title"),
                description: t("orderDetail.times.toast.deleted.description")
            });

            // Reload order details to update admin fees and other calculations
            const updatedOrderDetails = await getOrderDetails(token, orderId);
            if (updatedOrderDetails) {
                setOrderDetails(updatedOrderDetails);
            }
            
            // Trigger costs refresh
            setCostsRefreshTrigger(prev => prev + 1);
            
            await loadOrderTimes();
            await loadAuditLogs();
            setIsOrderTimeDeleteDialogOpen(false);
            setDeletingOrderTime(null);
        } catch (error) {
            log.error("Error deleting order time:", error);
            toast({
                title: t("orderDetail.times.toast.error.title"),
                description: t("orderDetail.times.toast.error.description"),
                variant: "destructive"
            });
        }
    }

    function handleCancelDeleteOrderTime() {
        setIsOrderTimeDeleteDialogOpen(false);
        setDeletingOrderTime(null);
    }

    useEffect(() => {
        async function loadData() {
            setIsLoading(true)

            try {
                const [orderDetails, orgDetails, emailCapability] = await Promise.all([
                    getOrderDetails(token, orderId!),
                    getOrganizationDetails(token).catch(() => null),
                    getEmailCapability(token).catch(() => null),
                ])

                if (orderDetails) {
                    setOrderDetails(orderDetails)
                    setHasOrganizationDetails(!!orgDetails)
                    setEmailCapabilityAvailable(!!emailCapability?.available)
                    await Promise.all([
                        loadOrderTimes(),
                        loadAuditLogs(),
                        loadEmailLogs(),
                    ])
                }
            } catch (error) {
                log.error("Error loading order details:", error);
                // FIXME: Better error handling
            } finally {
                setIsLoading(false)
            }
        }

        loadData()
    }, [orderId, token]);

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-64">
                <Text className="text-gray-500">{t("orderDetails.loading")}</Text>
            </div>
        )
    }

    if (!orderDetails) {
        return (
            <div className="flex justify-center items-center h-64">
                <Text className="text-red-500">{t("orderDetails.loading-issue")}</Text>
            </div>
        )
    }

    function getStatusBadgeColor(status: string): "red" | "green" | "blue" | "zinc" {
        switch (status) {
            case 'Open':
                return 'blue';
            case 'Completed':
                return 'green';
            case 'Cancelled':
                return 'red';
            default:
                return 'zinc';
        }
    }

    function getOrderStatusText(orderStatus?: string) {
        const key = orderStatus || "unknown";
        return t(`orderDetails.status.${key}`);
    }

    function getAuditLogActionText(actionType: string): string {
        const actionMap: Record<string, string> = {
            'CREATED': t("orderDetails.sections.history.auditLog.actions.created"),
            'UPDATED_BASIC_INFO': t("orderDetails.sections.history.auditLog.actions.updatedBasicInfo"),
            'UPDATED_CLIENT': t("orderDetails.sections.history.auditLog.actions.updatedClient"),
            'UPDATED_HELPER': t("orderDetails.sections.history.auditLog.actions.updatedHelper"),
            'UPDATED_SERVICE': t("orderDetails.sections.history.auditLog.actions.updatedService"),
            'UPDATED_DATES': t("orderDetails.sections.history.auditLog.actions.updatedDates"),
            'ADDED_TIME_ENTRY': t("orderDetails.sections.history.auditLog.actions.addedTimeEntry"),
            'UPDATED_TIME_ENTRY': t("orderDetails.sections.history.auditLog.actions.updatedTimeEntry"),
            'REMOVED_TIME_ENTRY': t("orderDetails.sections.history.auditLog.actions.removedTimeEntry"),
            'UPDATED_MILEAGE': t("orderDetails.sections.history.auditLog.actions.updatedMileage"),
            'UPDATED_EXPENSES': t("orderDetails.sections.history.auditLog.actions.updatedExpenses"),
            'UPDATED_DONATION': t("orderDetails.sections.history.auditLog.actions.updatedDonation"),
            'UPDATED_PAYMENT_METHOD': t("orderDetails.sections.history.auditLog.actions.updatedPaymentMethod"),
            'CANCELLED': t("orderDetails.sections.history.auditLog.actions.cancelled"),
            'COMPLETED': t("orderDetails.sections.history.auditLog.actions.completed"),
            'REOPENED': t("orderDetails.sections.history.auditLog.actions.reopened"),
            'IMPORTED': t("orderDetails.sections.history.auditLog.actions.imported")
        };

        return actionMap[actionType] || actionType;
    }

    function getAuditLogBadgeColor(actionType: string): "green" | "blue" | "red" | "amber" | "zinc" {
        switch (actionType) {
            case 'CREATED':
            case 'IMPORTED':
                return 'green'
            case 'COMPLETED':
                return 'green'
            case 'CANCELLED':
            case 'REMOVED_TIME_ENTRY':
                return 'red'
            case 'REOPENED':
                return 'amber'
            default:
                return 'blue'
        }
    }

    function getAuditLogDotColor(actionType: string): string {
        switch (actionType) {
            case 'CREATED':
            case 'IMPORTED':
            case 'COMPLETED':
                return 'bg-green-500'
            case 'CANCELLED':
            case 'REMOVED_TIME_ENTRY':
                return 'bg-red-500'
            case 'REOPENED':
                return 'bg-amber-500'
            default:
                return 'bg-blue-500'
        }
    }

    function getAuditLogDescription(description: string): string {
        try {
            // Versuche den JSON zu parsen
            const data = JSON.parse(description);
            if (data.key) {
                // Formatiere Daten in den Parametern, falls vorhanden
                let params = data.params;
                if (params) {
                    // Formatiere details Parameter, der Datum und Zeiten enthalten könnte
                    if (params.details && typeof params.details === 'string') {
                        // Format: "2025-08-03: 09:00 - 11:00"
                        const match = params.details.match(/^(\d{4}-\d{2}-\d{2}): (\d{2}:\d{2}) - (\d{2}:\d{2})$/);
                        if (match) {
                            const [, date, startTime, endTime] = match;
                            params = {
                                ...params,
                                details: `${formatDateString(date)}: ${startTime} - ${endTime}`
                            };
                        }
                    }
                    return t(`orderDetails.sections.history.auditLog.descriptions.${data.key}`, params);
                }
                return t(`orderDetails.sections.history.auditLog.descriptions.${data.key}`);
            }
        } catch {
            // Falls es kein JSON ist, gib den originalen Text zurück (z.B. bei Reopened mit Grund)
        }
        return description;
    }

    return (
        <div className="container mx-auto py-6 max-w-4xl space-y-6">
            <div className={"w-full text-right"}>
                <OrderActionsButton
                    onEdit={handleEditOrder}
                    onCancel={handleCancelOrder}
                    onComplete={handleCompleteOrder}
                    onReopen={handleReopenOrder}
                    onSendEmail={() => setIsSendEmailDialogOpen(true)}
                    orderData={orderDetails}
                    orderTimesCount={orderTimes.length}
                    hasOrganizationDetails={hasOrganizationDetails}
                    emailCapabilityAvailable={emailCapabilityAvailable}
                />
            </div>

            <Card className="w-full">
                <div className="p-6">
                    <div className="flex flex-col md:flex-row md:items-center gap-6">
                        <Avatar className="h-24 w-24">
                            <AvatarFallback>
                                <ListTodo size={48}/>
                            </AvatarFallback>
                        </Avatar>
                        <div className="space-y-2 flex-1">
                            <div>
                                <Heading
                                    data-testid="order-number">{t("orderDetails.orderPrefix")} {orderDetails.orderNo}</Heading>
                            </div>
                            <div className="flex items-center gap-4 text-gray-500 dark:text-gray-400">
                                <div className="flex items-center gap-2">
                                    <CalendarIcon className="h-4 w-4"/>
                                    <Text>{formatDateString(orderDetails.orderDate)}</Text>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Activity className="h-4 w-4"/>
                                    <Badge
                                        data-testid={orderDetails.orderStatus === 'Completed' ? 'order-status-completed' : 'order-status'}
                                        color={getStatusBadgeColor(orderDetails.orderStatus || 'unbekannt')}>
                                        {getOrderStatusText(orderDetails.orderStatus) /*|| 'Status unbekannt' */}
                                    </Badge>
                                </div>
                                {orderDetails.orderStatus === 'Completed' && orderDetails.voucherNumber && (
                                    <div className="flex items-center gap-2">
                                        <Hash className="h-4 w-4"/>
                                        <Text>{t("orderDetails.voucherNumber")}: {orderDetails.voucherNumber}</Text>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </Card>

            {orderDetails.cancellationDetails && (
                <Card data-testid="order-cancelled-info"
                      className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/20">
                    <div className="p-6">
                        <div className="flex items-center gap-3 mb-4">
                            <AlertTriangle className="h-5 w-5 text-red-500"/>
                            <Subheading className="text-red-700 dark:text-red-400">
                                {t("orderDetails.sections.cancelled.heading")}
                            </Subheading>
                        </div>
                        <div className="flex items-center gap-3">
                            <User className="h-4 w-4 text-red-500"/>
                            <div>
                                <Text className="font-medium">
                                    {t("orderDetails.sections.cancelled.cancelled-by")} {orderDetails.cancellationDetails.user.name}
                                </Text>
                                <Text className="text-gray-600 dark:text-gray-400">
                                    {t("orderDetails.sections.cancelled.cancelled-at", {
                                        date: formatDateString(orderDetails.cancellationDetails.date),
                                        time: formatTimeString(orderDetails.cancellationDetails.date)
                                    })}
                                </Text>
                            </div>
                        </div>
                    </div>
                </Card>
            )}

            {orderDetails.completionDetails && (
                <Card data-testid="order-completed-info"
                      className="border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950/20">
                    <div className="p-6">
                        <div className="flex items-center gap-3 mb-4">
                            <CheckCircle className="h-5 w-5 text-green-500"/>
                            <Subheading className="text-green-700 dark:text-green-400">
                                {orderDetails.settlementDate 
                                    ? t("orderDetails.sections.completed.headingWithDate", {
                                        date: formatDateString(orderDetails.settlementDate)
                                      })
                                    : t("orderDetails.sections.completed.heading")
                                }
                            </Subheading>
                        </div>
                        <div className="flex items-center gap-3">
                            <User className="h-4 w-4 text-green-500"/>
                            <div>
                                <Text className="font-medium">
                                    {t("orderDetails.sections.completed.completed-by")} {orderDetails.completionDetails.user.name}
                                </Text>
                            </div>
                        </div>
                    </div>
                </Card>
            )}

            <Card>
                <div className="p-6">
                    <div className="relative mb-6">
                        <div className="text-2xl font-semibold leading-none tracking-tight" role="heading">
                            {t("orderDetails.sections.meta.serviceAndSubmission")}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-4">
                            <div className="flex items-center gap-3">
                                <ClipboardList className="h-4 w-4 text-gray-500"/>
                                <div>
                                    <Text className="font-medium">{t("orderDetails.sections.meta.serviceLabel")}</Text>
                                    <Text
                                        className="text-gray-600 dark:text-gray-400">{orderDetails.service.name}</Text>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <FolderInput className="h-4 w-4 text-gray-500"/>
                                <div>
                                    <Text
                                        className="font-medium">{t("orderDetails.sections.meta.submissionChannelLabel")}</Text>
                                    <Text
                                        className="text-gray-600 dark:text-gray-400">{orderDetails.submissionChannel.name}</Text>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-start gap-3">
                                <MessageSquare className="h-4 w-4 text-gray-500 mt-1"/>
                                <div className="flex-1">
                                    <Text className="font-medium">{t("orderDetails.sections.meta.commentsLabel")}</Text>
                                    {orderDetails.comments ? (
                                        <Text className="text-gray-600 dark:text-gray-400 whitespace-pre-wrap">
                                            {orderDetails.comments}
                                        </Text>
                                    ) : (
                                        <Text className="italic text-gray-500">
                                            {t("orderDetails.sections.meta.noComments")}
                                        </Text>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                    <div className="p-6">
                        <div className="relative mb-6">
                            <div className="text-2xl font-semibold leading-none tracking-tight" role="heading">
                                {t("orderDetails.sections.client.heading")}
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div className="flex items-center gap-3">
                                <User className="h-4 w-4 text-gray-500"/>
                                <Text
                                    className="font-medium">{orderDetails.client.firstName} {orderDetails.client.lastName}</Text>
                            </div>
                            <div className="flex items-center gap-3">
                                <Hash className="h-4 w-4 text-gray-500"/>
                                <Text>{t("orderDetails.sections.client.memberNumber")} {orderDetails.client.memberNo}</Text>
                            </div>
                            {(orderDetails.client.points ?? -1) >= 0 && (
                                <div className="flex items-center gap-3">
                                    <Coins className="h-4 w-4 text-gray-500"/>
                                    <Text>{t("orderDetails.sections.client.points")} {orderDetails.client.points}</Text>
                                </div>
                            )}
                        </div>
                    </div>
                </Card>

                <Card>
                    <div className="p-6">
                        <div className="relative mb-6">
                            <div className="text-2xl font-semibold leading-none tracking-tight" role="heading">
                                {t("orderDetails.sections.helper.heading")}
                            </div>
                        </div>

                        <div className="space-y-4">
                            {orderDetails.helper ? (
                                <>
                                    <div className="flex items-center gap-3">
                                        <User className="h-4 w-4 text-gray-500"/>
                                        <Text
                                            className="font-medium">{orderDetails.helper.firstName} {orderDetails.helper.lastName}</Text>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <Hash className="h-4 w-4 text-gray-500"/>
                                        <Text>{t("orderDetails.sections.client.memberNumber")} {orderDetails.helper.memberNo}</Text>
                                    </div>
                                </>
                            ) : (
                                <div className="flex items-center gap-3">
                                    <UserCog className="h-4 w-4 text-gray-500"/>
                                    <Text
                                        className="italic text-gray-500">{t("orderDetails.sections.helper.noHelperAssigned")}</Text>
                                </div>
                            )}
                        </div>
                    </div>
                </Card>
            </div>

            {/* OrderTimes Section */}
            <Card>
                <div className="p-6">
                    <div className="relative mb-6">
                        <div className="text-2xl font-semibold leading-none tracking-tight" role="heading">
                            {t("orderDetail.times.heading")}
                        </div>
                    </div>

                    <OrderTimesTable
                        orderTimes={orderTimes}
                        onAdd={handleAddOrderTime}
                        onEdit={handleEditOrderTime}
                        onDelete={handleDeleteOrderTime}
                        isOrderCancelled={orderDetails?.orderStatus === "Cancelled"}
                        isOrderCompleted={orderDetails?.orderStatus === "Completed"}
                        hasHelper={!!orderDetails?.helper}
                        onSortingChange={(sortBy, sortOrder) => loadOrderTimes(sortBy, sortOrder)}
                    />
                </div>
            </Card>

            {/* Order Costs Section */}
            <OrderCostsSection
                order={orderDetails}
                refreshTrigger={costsRefreshTrigger}
                onOrderUpdate={(updatedOrder) => {
                    setOrderDetails(updatedOrder);
                    // Reload audit logs to show cost changes
                    loadAuditLogs();
                }}
            />

            <Card>
                <div className="p-6">
                    <div className="relative mb-6">
                        <div className="text-2xl font-semibold leading-none tracking-tight" role="heading">
                            {t("orderDetails.sections.history.heading")}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <div className="flex items-center gap-2 text-sm font-medium">
                                <Clock className="h-4 w-4"/>
                                <Subheading level={3}>{t("orderDetails.sections.history.created")}</Subheading>
                            </div>
                            <div className="pl-6">
                                <Text>{t("orderDetails.sections.history.by")} {orderDetails.creationDetails.user.name}</Text>
                                <Text className="text-gray-600 dark:text-gray-400">
                                    {t("orderDetails.sections.history.on")} {formatDateString(orderDetails.creationDetails.date)} {t("orderDetails.sections.history.at")} {formatTimeString(orderDetails.creationDetails.date)} {t("orderDetails.sections.history.clock")}
                                </Text>
                            </div>
                        </div>

                        {orderDetails.updateDetails && (
                            <div className="space-y-2">
                                <div className="flex items-center gap-2 text-sm font-medium">
                                    <Clock className="h-4 w-4"/>
                                    <Subheading level={3}>{t("orderDetails.sections.history.lastModified")}</Subheading>
                                </div>
                                <div className="pl-6">
                                    <Text>{t("orderDetails.sections.history.by")} {orderDetails.updateDetails.user.name}</Text>
                                    <Text className="text-gray-600 dark:text-gray-400">
                                        {t("orderDetails.sections.history.on")} {formatDateString(orderDetails.updateDetails.date)} {t("orderDetails.sections.history.at")} {formatTimeString(orderDetails.updateDetails.date)} {t("orderDetails.sections.history.clock")}
                                    </Text>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Email Send Logs Section */}
                    {emailLogs.length > 0 && (
                        <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                            <Button
                                plain
                                onClick={() => setIsEmailLogsExpanded(!isEmailLogsExpanded)}
                                className="flex items-center gap-2 text-sm font-medium p-0 h-auto hover:bg-transparent"
                                data-testid="email-logs-toggle"
                            >
                                {isEmailLogsExpanded ? (
                                    <>
                                        <ChevronUp className="h-4 w-4"/>
                                        <Text>{t("orderDetails.sections.history.emailLog.hide")}</Text>
                                    </>
                                ) : (
                                    <>
                                        <ChevronDown className="h-4 w-4"/>
                                        <Text>{t("orderDetails.sections.history.emailLog.show")}</Text>
                                    </>
                                )}
                            </Button>

                            {isEmailLogsExpanded && (
                                <div className="mt-4 space-y-4" data-testid="email-logs-list">
                                    {emailLogs.map((emailLog) => (
                                        <div key={emailLog.id} className="flex gap-3 py-2">
                                            <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${emailLog.status === "SUCCESS" ? "bg-green-500" : "bg-red-500"}`}></div>
                                            <div className="flex-1 space-y-1">
                                                <div className="flex items-center gap-2">
                                                    <Badge color={emailLog.status === "SUCCESS" ? "green" : "red"}>
                                                        {emailLog.status === "SUCCESS"
                                                            ? t("orderDetails.sections.history.emailLog.statusSuccess")
                                                            : t("orderDetails.sections.history.emailLog.statusFailure")}
                                                    </Badge>
                                                    <Text className="text-xs text-gray-500">
                                                        {formatDateString(emailLog.createdAt)} {formatTimeString(emailLog.createdAt)}
                                                    </Text>
                                                </div>
                                                <div className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400">
                                                    <Mail className="h-3 w-3"/>
                                                    <Text>{emailLog.recipientEmail}</Text>
                                                </div>
                                                <Text className="text-sm text-gray-600 dark:text-gray-400">
                                                    {emailLog.subject}
                                                </Text>
                                                {emailLog.failureSummary && (
                                                    <Text className="text-sm text-red-600 dark:text-red-400">
                                                        {emailLog.failureSummary}
                                                    </Text>
                                                )}
                                                <Text className="text-xs text-gray-500">
                                                    {t("orderDetails.sections.history.by")} {emailLog.createdByName ?? emailLog.createdBy}
                                                    {" · "}{emailLog.provider}
                                                </Text>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Audit Logs Section */}
                    {auditLogs.length > 0 && (
                        <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                            <Button
                                plain
                                onClick={() => setIsAuditLogsExpanded(!isAuditLogsExpanded)}
                                className="flex items-center gap-2 text-sm font-medium p-0 h-auto hover:bg-transparent"
                                data-testid="audit-logs-toggle"
                            >
                                {isAuditLogsExpanded ? (
                                    <>
                                        <ChevronUp className="h-4 w-4"/>
                                        <Text>{t("orderDetails.sections.history.auditLog.hide")}</Text>
                                    </>
                                ) : (
                                    <>
                                        <ChevronDown className="h-4 w-4"/>
                                        <Text>{t("orderDetails.sections.history.auditLog.show")}</Text>
                                    </>
                                )}
                            </Button>

                            {isAuditLogsExpanded && (
                                <div className="mt-4 space-y-4" data-testid="audit-logs-list">
                                    {auditLogs.map((auditLog) => (
                                        <div key={auditLog.id} className="flex gap-3 py-2">
                                            <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${getAuditLogDotColor(auditLog.actionType)}`}></div>
                                            <div className="flex-1 space-y-1">
                                                <div className="flex items-center gap-2">
                                                    <Badge color={getAuditLogBadgeColor(auditLog.actionType)}>
                                                        {getAuditLogActionText(auditLog.actionType)}
                                                    </Badge>
                                                    <Text className="text-xs text-gray-500">
                                                        {formatDateString(auditLog.createdAt)} {formatTimeString(auditLog.createdAt)}
                                                    </Text>
                                                </div>
                                                {auditLog.description && (
                                                    <Text className="text-sm text-gray-600 dark:text-gray-400">
                                                        {getAuditLogDescription(auditLog.description)}
                                                    </Text>
                                                )}
                                                <Text className="text-xs text-gray-500">
                                                    {t("orderDetails.sections.history.by")} {auditLog.createdBy}
                                                </Text>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </Card>

            <Divider className="my-10" soft/>

            <section className="flex justify-end gap-4">
                <Button
                    plain
                    aria-label={tCommon("buttons.back.ariaLabel")}
                    href="/orders"
                    role="navigation"
                    data-testid="order-back-button"
                >
                    {tCommon("buttons.back.text")}
                </Button>
            </section>

            {orderDetails && (
                <OrderEditDialog
                    isOpen={isEditDialogOpen}
                    onSave={(updatedOrder) => {
                        setOrderDetails(updatedOrder);
                        // Reload audit logs to show changes
                        loadAuditLogs();
                        // Show success message
                        toast({
                            title: t("orderForm.edit.success.title"),
                            description: t("orderForm.edit.success.description")
                        });
                    }}
                    onClose={() => setIsEditDialogOpen(false)}
                    orderData={orderDetails}
                />
            )}

            {/* OrderTime Dialogs */}
            <OrderTimeDialog
                isOpen={isOrderTimeDialogOpen}
                orderTime={editingOrderTime}
                orderDate={orderDetails?.orderDate || ""}
                onSave={handleSaveOrderTime}
                onClose={() => {
                    setIsOrderTimeDialogOpen(false);
                    setEditingOrderTime(undefined);
                }}
            />

            <OrderTimeDeleteDialog
                isOpen={isOrderTimeDeleteDialogOpen}
                orderTime={deletingOrderTime}
                onConfirm={handleConfirmDeleteOrderTime}
                onCancel={handleCancelDeleteOrderTime}
            />

            {orderDetails && (
                <OrderCompleteDialog
                    isOpen={isCompleteDialogOpen}
                    onConfirm={handleConfirmCompleteOrder}
                    onClose={() => setIsCompleteDialogOpen(false)}
                    orderDetails={orderDetails}
                    orderTimes={orderTimes}
                />
            )}

            {orderDetails && (
                <ReopenOrderDialog
                    orderId={orderDetails.id}
                    isOpen={isReopenDialogOpen}
                    onClose={() => setIsReopenDialogOpen(false)}
                    onSuccess={handleReopenSuccess}
                />
            )}

            {orderDetails && (
                <OrderSendEmailDialog
                    orderId={orderDetails.id}
                    isOpen={isSendEmailDialogOpen}
                    onClose={() => setIsSendEmailDialogOpen(false)}
                    onSuccess={() => { loadAuditLogs(); loadEmailLogs(); }}
                />
            )}
        </div>
    )
}
